import { normalizeSportName } from './drillTemplates';
import { deriveFocusSkills } from './skillMapping';
import { buildLessonOutcomeSignals } from './lessonOutcomeSignals';
import { aggregateProgressFromReviews } from './progressAnalysis';
import { buildProgressionRecommendationFromInput } from './progressionEngine';
import { buildReviewToProgressionInput } from './reviewToProgression';

function incrementCount(map, key, amount = 1) {
  if (!key) return;
  map.set(key, (map.get(key) || 0) + amount);
}

function sortCounts(map) {
  return Array.from(map.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([label, count]) => ({ label, count }));
}

function normalizeLabel(value) {
  if (!value) return '';
  const text = String(value).trim();
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function getReviewedVideos(videos = []) {
  return (videos || [])
    .filter((video) => video?.status === 'reviewed')
    .sort((a, b) => {
      const aTime = a.reviewedAt?.seconds || a.createdAt?.seconds || 0;
      const bTime = b.reviewedAt?.seconds || b.createdAt?.seconds || 0;
      return bTime - aTime;
    });
}

// Pass 77 — top focus areas are HUMAN-only: canonical human aggregate focus areas (video.analysis signals) first,
// else human review drills + explicitly-linked human focus skills. NO aiReview.focusAreas / knowledgeMatches and
// NO free-text timestamp-comment keyword themes (all removed — AI is non-authoritative for progression).
function buildTopFocusAreas(recentReviews = [], feedbackMap = {}, aggregate = {}) {
  if ((aggregate.focusAreas || []).length > 0) {
    return aggregate.focusAreas;
  }

  const focusCounts = new Map();
  recentReviews.forEach((video) => {
    (video.reviewDrills || []).forEach((drill) => {
      incrementCount(focusCounts, normalizeLabel(drill.topic), 2);
      (drill.focusAreas || []).forEach((area) => incrementCount(focusCounts, normalizeLabel(area), 2));
    });
    deriveFocusSkills(video, feedbackMap[video.id] || []).forEach((skill) => {
      incrementCount(focusCounts, skill.name, skill.score);
    });
  });

  return sortCounts(focusCounts).slice(0, 4).map((item) => ({ ...item, key: item.label }));
}

function resolveProgressTrackingOptions(optionsOrFeedbackMap = {}) {
  if (
    optionsOrFeedbackMap &&
    !Array.isArray(optionsOrFeedbackMap) &&
    !Object.prototype.hasOwnProperty.call(optionsOrFeedbackMap, 'feedbackMap')
  ) {
    return { feedbackMap: optionsOrFeedbackMap, lessonInstances: [], sport: '' };
  }

  return {
    feedbackMap: optionsOrFeedbackMap?.feedbackMap || {},
    lessonInstances: optionsOrFeedbackMap?.lessonInstances || [],
    sport: optionsOrFeedbackMap?.sport || '',
  };
}

export function buildStudentProgressSummary(videos = [], optionsOrFeedbackMap = {}) {
  const { feedbackMap, lessonInstances, sport: selectedSport } = resolveProgressTrackingOptions(optionsOrFeedbackMap);
  const reviewedVideos = getReviewedVideos(videos);
  const recentReviews = reviewedVideos.slice(0, 5);

  // Pass 77 — sport is EXPLICIT: the caller's selected sport is authoritative and flows through the whole
  // progression contract (no dominant-sport guessing when it is known). Fallback to the dominant reviewed sport
  // only when the caller did not supply one (backward-compat, e.g. instructor pages).
  const sport = normalizeSportName(selectedSport || '') || normalizeSportName(recentReviews[0]?.sport || '');

  const reviewProgressSummary = aggregateProgressFromReviews(videos, { feedbackMap });
  const lessonSignals = buildLessonOutcomeSignals(lessonInstances, { sport });

  const progressSummary = {
    ...reviewProgressSummary,
    sport,
    lessonSignals,
    completedLessonCount: lessonSignals.completedLessonCount,
    lessonReinforcementText: lessonSignals.summaryText || '',
  };

  const topFocusAreas = buildTopFocusAreas(recentReviews, feedbackMap, reviewProgressSummary);
  // ONE canonical evidence build (no aggregate+raw double-count): sport + videos + lessonSignals go to the contract.
  const progressionInput = buildReviewToProgressionInput(progressSummary, videos, { feedbackMap, sport, lessonInstances });
  const progressionRecommendation = buildProgressionRecommendationFromInput(progressionInput, { feedbackMap });

  return {
    reviewedCount: reviewedVideos.length,
    recentReviewCount: recentReviews.length,
    dominantSport: progressionInput?.sport || sport || '',
    currentLevel: progressionInput?.currentLevel || '',
    levelBasis: progressionInput?.levelBasis || 'none',
    hasProgressionEvidence: !!progressionInput?.hasEvidence,
    topFocusAreas,
    progressionInput,
    progressionRecommendation,
    recommendationReason: progressionRecommendation?.recommendationReason || progressionInput?.recommendationReason || '',
    activeTrack: progressionRecommendation?.activeTrack || null,
    currentSkill: progressionRecommendation?.currentSkill || null,
    nextSkill: progressionRecommendation?.nextSkill || null,
    lessonFlow: progressionRecommendation?.lessonFlow || null,
    lessonFlowStages: progressionRecommendation?.lessonFlow?.stages || [],
    lessonSignals,
    lessonReinforcementText: lessonSignals.summaryText || '',
    completedLessonCount: lessonSignals.completedLessonCount,
    ...progressSummary,
  };
}
